import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, LinearProgress, MenuItem, Select, Tooltip } from "@mui/material";
import AssessmentList from "./AssessmentList";
import { addObservers, fetchAssessments, fetchmemberDetails, updateAssessmentStatus, fetchDeployments } from "../../api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Dropdown from "../../Assets/Images/dropdown-icon.svg";
import { createAssessmentAndUser, updateAssessment } from '../../api/index'
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { elements } from "chart.js";
import { Close, InfoOutlined } from "@mui/icons-material";
import { DashboardContext } from "../../Context/DashboardContext";
import { toast } from "react-toastify";
import { useMemo } from "react";
import GroupReportContext from "../../Context/GroupReportContext";
import AuthContext from "../../Context/AuthContext";
import { validateName } from "../../utils/helper";
import * as XLSX from "xlsx";

const Assessment = ({ clientId, clientData, setValue, onClientDataUpdate }) => {

  //other hooks
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardCtx = useContext(DashboardContext)
  const groupReportCtx = useContext(GroupReportContext)
  const authCtx = useContext(AuthContext)
  const { id } = useParams()

  //useStates
  const [assessmentStep, setAssessmentStep] = useState(0);
  const [observers, setObservers] = useState(0);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('');
  const [relationship, setRelationship] = useState("");
  const [showObserverButton, setShowObserverButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const firstRenderRef = useRef(true);
  const [observersList, setObserversList] = useState([]);
  const [isSameAsClient, setIsSameAsClient] = useState(false);
  const [open, setOpen] = useState(false);
  const [observersOpen, setObserversOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [editForm, setEditForm] = useState(false)
  const [hideCheckbox, setHideCheckbox] = useState(false)
  const [observersType, setObserversType] = useState(['360 Assessment Peer', '360 Assessment Manager', '360 Assessment Direct Report', '360 Assessment Other'])
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkTotal, setBulkTotal] = useState(0);
  const [bulkIssues, setBulkIssues] = useState([]);
  const [showBulkIssues, setShowBulkIssues] = useState(false);

  const [bulkNeedsConfirm, setBulkNeedsConfirm] = useState(false);
  const bulkInputRef = useRef(null);

  const [assessmentData, setAssessmentData] = useState({
    name: "",
    email: "",
  });
  const [assessmentList, setAssessmentList] = useState([]);

  //Regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const nameRegex = /^[a-zA-Z\s.''\-]{2,}$/;

  //useEffects
  useEffect(() => {
    if (selectedAssessmentType === "360 Assessment") {
      setObservers((currentCount) => currentCount || 4)
    } else {
      setObservers(0)
    }
  }, [selectedAssessmentType]);

  useEffect(() => {
    // // console.log("Component mounted or updated");
  });

  useEffect(() => {
    if (firstRenderRef?.current && clientData?.id) {
      // // console.log("Fetching assessments for the first time");
      fetchApi();
      firstRenderRef.current = false;
    } else {
      // // console.log("Setting assessment list from clientData");
    }
  }, [clientData]);

  useEffect(() => {
    if (editForm && editForm.designation?.includes("Assessment")) {
      setAssessmentStep(1);
      setSelectedAssessmentType(editForm.designation);

      const self = editForm.members?.find(member => member.designation === 'Self-Assessment') || editForm;

      setAssessmentData({
        name: self.userName || '',
        email: self.email || '',
      });

      if (editForm.designation === '360 Assessment') {
        const observers = editForm.members?.filter(
          member => member.designation !== 'Self-Assessment'
        ) || [];

        setObservers(observers.length);
        setObserversList(
          observers.map(observer => ({
            name: observer.userName,
            email: observer.email,
            role: observer.designation,
            assessment_hash: observer.assessment_hash,
          }))
        );
      } else {
        setObservers(0);
        setObserversList([]);
      }
    }
  }, [editForm]);

  useEffect(() => {
    updateClientData()
  }, [dashboardCtx.unreadCount, id])


  // // console.log('editForm', editForm)

  //functions
  const updateClientData = async () => {
    setIsLoading(true)
    try {
      const clientData = await fetchDeployments();
      const currentClient = clientData.find(c => c.id == clientId);

      if (currentClient) {
        const assessments = await fetchAssessments(currentClient.id);
        const completeAssessments = assessments.filter(a => a.status === "complete");

        const assessmentDetails = assessments?.map((el) => {
          if (el?.designation === "360 Assessment") {
            return {
              ...el,
              members: [{ designation: "Self-Assessment", userName: el?.userName, email: el?.email, status: el?.status, assessment_hash: el?.assessment_hash }, ...(el.members || [])]
            };
          } else {
            return el;
          }
        })?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        // // console.log("assessmentDetails", assessmentDetails)

        setAssessmentList(assessmentDetails);

        const updatedClient = {
          ...currentClient,
          totalAssessments: assessments?.length || 0,
          completeAssessments: completeAssessments.length,
          pendingAssessment: (assessments?.length || 0) - completeAssessments.length,
          assessments
        };

        localStorage.setItem("clientData", JSON.stringify(updatedClient));

        // Notify parent component about the updated client data
        if (onClientDataUpdate) {
          onClientDataUpdate(updatedClient);
        }

        // Dispatch custom event to notify other components about localStorage change
        window.dispatchEvent(new CustomEvent('clientDataUpdated', {
          detail: { updatedClient }
        }));

        setIsLoading(false)
        return updatedClient;
      }

    } catch (error) {
      setIsLoading(false)
      console.error('Error updating client data:', error);
    }
  };

  const fetchApi = useCallback(async () => {
    // // console.log("Fetch API called");
    setIsLoading(true);
    setIsSameAsClient(false);
    const MIN_LOADING_TIME = 1500;
    const startTime = Date.now();

    try {
      updateClientData();
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching assessments:", error);
      setAssessmentList([]);
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
    }
  }, [clientId]);

  // Spreadsheet imports deliberately only populate the same form state used for
  // manual entry. This keeps validation and assessment creation identical for
  // uploaded and hand-entered respondents.
  const loadBulkRow = (row, remainingRows = []) => {
    // Never render more observer inputs than the 12 allowed. Extra rows are
    // reported in the issues panel when the file is uploaded.
    const observers = (row.observers || []).slice(0, 12);
    setSelectedAssessmentType(row.assessmentType);
    setAssessmentData({ name: row.name, email: row.email });
    setObservers(observers.length);
    setObserversList(observers);
    // Show problems from the file under the matching inputs straight away.
    const loadErrors = getFormErrors(row.assessmentType, row, observers);
    if (row.assessmentType === "360 Assessment" && observers.length < 4) {
      loadErrors.observers = { ...loadErrors.observers, overall: "Minimum 4 observers required" };
    }
    setErrors(loadErrors);
    setIsSameAsClient(false);
    setBulkRows(remainingRows);
    setAssessmentStep(1);
  };

  const normaliseAssessmentType = (value) => {
    const type = String(value || "").trim().toLowerCase();
    if (["self", "self assessment", "self-assessment"].includes(type)) return "Self-Assessment";
    if (["360", "360 assessment", "360-assessment"].includes(type)) return "360 Assessment";
    return "";
  };

  // Relationships are a fixed list. Unknown values are left blank so the form
  // asks for a selection instead of silently defaulting to "Other".
  const normaliseObserverDesignation = (value) => {
    const designation = String(value || "").trim().toLowerCase();
    if (!designation) return "";
    const match = {
      peer: "360 Assessment Peer",
      manager: "360 Assessment Manager",
      "direct report": "360 Assessment Direct Report",
      other: "360 Assessment Other",
    };
    return match[designation.replace(/^360 assessment\s*/, "")] || "";
  };


  // Same name rules the form applies, so the file scan and the form agree.
  const getNameError = (value) => {
    const nameError = validateName(value);
    if (nameError && nameError !== 'Please enter a valid name.') return nameError;
    return nameRegex.test(value.trim()) ? "" : "Please enter a valid name.";
  };

  // "row 2", "rows 2-5" or "rows 6, 11"
  const formatRows = (rowNumbers) => {
    const ranges = [];
    rowNumbers.forEach((row) => {
      const last = ranges[ranges.length - 1];
      if (last && row === last[1] + 1) last[1] = row;
      else ranges.push([row, row]);
    });
    const text = ranges.map(([from, to]) => (from === to ? `${from}` : `${from}-${to}`)).join(", ");
    return `${rowNumbers.length === 1 ? "row" : "rows"} ${text}`;
  };

  const collectBulkIssues = (parsedRows, respondents, observerSpreadsheet) => {
    const issues = [];
    const addIssue = (row, message, needsConfirm = false) => issues.push({ row, message, needsConfirm });

    parsedRows.forEach((row) => {
      if (!observerSpreadsheet) {
        if (!row.name) addIssue(row.row, "Respondent name is missing.");
        else if (getNameError(row.name)) addIssue(row.row, `Respondent name "${row.name}" is invalid. ${getNameError(row.name)}`);
        if (!row.email) addIssue(row.row, "Respondent email is missing.");
        else if (!emailRegex.test(row.email)) addIssue(row.row, "Respondent email is invalid.");
        if (!row.assessmentType) {
          addIssue(row.row, row.assessmentTypeRaw
            ? `"${row.assessmentTypeRaw}" isn't a valid assessment type. Use Self-Assessment or 360 Assessment.`
            : "Assessment type is missing.");
        }
        return;
      }

      if (!row.observerName) addIssue(row.row, "Observer name is missing.");
      else if (getNameError(row.observerName)) addIssue(row.row, `Observer name "${row.observerName}" is invalid. ${getNameError(row.observerName)}`);
      if (!row.observerEmail) addIssue(row.row, "Observer email is missing.");
      else if (!emailRegex.test(row.observerEmail)) addIssue(row.row, "Observer email is invalid.");
      else if (row.email && row.observerEmail.toLowerCase() === row.email.toLowerCase()) {
        addIssue(row.row, "Observer email is the same as the respondent email.");
      }

      if (!row.observerDesignation) addIssue(row.row, "Relationship is missing.");
      else if (!normaliseObserverDesignation(row.observerDesignation)) {
        addIssue(row.row, `"${row.observerDesignation}" isn't a valid relationship. Use Peer, Manager, Direct Report, or Other.`);
      }
    });

    // Respondent details repeat on every observer row, so report them once per
    // respondent. Rows are grouped when either name or email matches, so also
    // flag rows whose other detail disagrees with the group (usually a typo).
    if (observerSpreadsheet) {
      respondents.forEach(({ name, email, sourceRows }) => {
        const label = name || email || "this respondent";
        ["name", "email"].forEach((field) => {
          const missing = sourceRows.filter((row) => !row[field]).map((row) => row.row);
          const first = sourceRows.find((row) => row[field]);
          // Blank cells are fine when another row gives the value; it is filled in.
          if (missing.length && !first) {
            addIssue(missing[0], `Respondent ${field} is missing for ${label} (${formatRows(missing)}).`);
          }
          if (!first) return;

          const invalid = {};
          sourceRows.forEach((row) => {
            if (!row[field]) return;
            if (row[field].toLowerCase() !== first[field].toLowerCase()) {
              addIssue(row.row, `Respondent ${field} "${row[field]}" doesn't match "${first[field]}" in row ${first.row}.`);
            }
            const error = field === "name" ? getNameError(row[field]) : (emailRegex.test(row[field]) ? "" : "invalid");
            if (error) (invalid[row[field]] = invalid[row[field]] || { error, rows: [] }).rows.push(row.row);
          });
          Object.entries(invalid).forEach(([value, { error, rows }]) => {
            addIssue(rows[0], `Respondent ${field} "${value}" is invalid for ${label} (${formatRows(rows)}).${field === "name" ? ` ${error}` : ""}`);
          });
        });
      });
    }

    if (observerSpreadsheet) {
      respondents.forEach((respondent) => {
        const label = respondent.name || respondent.email || "This respondent";
        const count = respondent.observers.length;
        const seenEmails = new Set();

        respondent.observers.forEach((observer, idx) => {
          const email = observer.email?.toLowerCase();
          if (!email) return;
          if (seenEmails.has(email)) addIssue(respondent.observerRows[idx], `Observer email is already listed for ${label}.`);
          seenEmails.add(email);
        });

        if (count < 4) addIssue(respondent.observerRows[0], `${label} has ${count} observer${count === 1 ? "" : "s"}. Each respondent needs at least 4.`);
        if (count > 12) {
          const skipped = respondent.observerRows.slice(12);
          const skippedText = skipped.length === 1 ? `row ${skipped[0]} was skipped` : `rows ${skipped.join(", ")} were skipped`;
          addIssue(skipped[0], `${label} has ${count} observers. Only the first 12 were loaded; ${skippedText}.`, true);
        }
      });
    }

    return issues.sort((a, b) => a.row - b.row);
  };

  const downloadBulkIssues = () => {
    const sheet = XLSX.utils.json_to_sheet(bulkIssues.map(({ row, message }) => ({ Row: row, Issue: message })));
    sheet["!cols"] = [{ wch: 8 }, { wch: 90 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Issues");
    const baseName = bulkFileName.replace(/\.[^.]+$/, "") || "Bulk upload";
    XLSX.writeFile(workbook, `${baseName} - issues.xlsx`);
  };

  const resetBulkUpload = () => {
    setBulkRows([]);
    setBulkFileName("");
    setBulkTotal(0);
    setBulkIssues([]);
    setShowBulkIssues(false);
    setBulkNeedsConfirm(false);
  };

  // Cancel on the confirm prompt drops the upload and blanks the form, but
  // keeps it open so a corrected sheet can be uploaded straight away.
  const cancelBulkUpload = () => {
    resetBulkUpload();
    setSelectedAssessmentType("");
    setAssessmentData({ name: "", email: "" });
    setObservers(0);
    setObserversList([]);
    setErrors({});
    setIsSameAsClient(false);
  };

  const handleBulkFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {

      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", sheets: 0 });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const records = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
      const parsedRows = records.map((record, index) => {
        const fields = Object.entries(record).reduce((acc, [key, value]) => {
          acc[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = String(value).trim();
          return acc;
        }, {});
        const assessmentTypeRaw = fields.assessmenttype || fields.type || "";
        const assessmentType = normaliseAssessmentType(assessmentTypeRaw);
        const name = fields.respondentname || fields.name || fields.fullname || "";
        const email = fields.respondentemail || fields.email || "";
        const observerName = fields.observername || "";
        const observerEmail = fields.observeremail || "";
        const observerDesignation = fields.observerdesignation || fields.observerrelationshipwithrespondent || fields.observersrelationshipwithrespondent || fields.relationship || "";

        const row = (record.__rowNum__ ?? index + 1) + 1;

        return { row, assessmentType, assessmentTypeRaw, name, email, observerName, observerEmail, observerDesignation };
      });

      const observerSpreadsheet = records.some((record) =>
        Object.keys(record).some((key) => {
          const column = String(key).toLowerCase().replace(/[^a-z0-9]/g, "");
          return ["observername", "observeremail", "observerdesignation", "observerrelationshipwithrespondent", "observersrelationshipwithrespondent"].includes(column);
        })
      );
      // Preserve every spreadsheet row, including incomplete ones. The form is
      // responsible for showing missing-field errors when the user submits.
      const rows = observerSpreadsheet
        ? parsedRows.reduce((groups, row) => {
          const email = row.email.toLowerCase();
          const name = row.name.toLowerCase();
          // A row with no respondent details continues the respondent above it.
          const group = !email && !name ? groups[groups.length - 1] : groups.find((candidate) =>
            (email && candidate.email.toLowerCase() === email) ||
            (name && candidate.name.toLowerCase() === name)
          );
          const respondent = group || {
            assessmentType: "360 Assessment",
            name: "",
            email: "",
            observers: [],
            observerRows: [],
            sourceRows: [],
          };

          // A later row can supply respondent details omitted on an earlier one.
          if (!respondent.name && row.name) respondent.name = row.name;
          if (!respondent.email && row.email) respondent.email = row.email;
          respondent.observers.push({
            name: row.observerName,
            email: row.observerEmail,
            role: normaliseObserverDesignation(row.observerDesignation),
          });
          respondent.observerRows.push(row.row);
          respondent.sourceRows.push(row);
          if (!group) groups.push(respondent);
          return groups;
        }, [])
        : parsedRows.map(({ assessmentType, name, email }) => ({ assessmentType, name, email }));

      if (!rows.length) {
        toast.error("No rows found. Use either the standard template or respondent and observer columns.");
        return;
      }

      // A file without respondent columns is not an upload template (for
      // example a different spreadsheet picked by mistake), so reject it.
      const columns = new Set(records.flatMap((record) => Object.keys(record).map((key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, ""))));
      const hasRespondentColumns = ["respondentname", "name", "fullname", "respondentemail", "email"].some((column) => columns.has(column));
      if (!hasRespondentColumns) {
        toast.error("This file doesn't match the template. Download the template, add your data, and upload it again.");
        return;
      }

      const issues = collectBulkIssues(parsedRows, rows, observerSpreadsheet);
      const loadedMessage = `${rows.length} assessment${rows.length === 1 ? "" : "s"} loaded.`;

      setBulkFileName(file.name);
      setBulkTotal(rows.length);
      setBulkIssues(issues);
      setShowBulkIssues(false);
      setBulkNeedsConfirm(issues.some((issue) => issue.needsConfirm));
      loadBulkRow(rows[0], rows.slice(1));
      if (issues.length) {
        toast.warning(`${loadedMessage} ${issues.length} issue${issues.length === 1 ? "" : "s"} found in your file.`);
      } else {
        toast.success(loadedMessage);
      }
    } catch (error) {
      console.error("Unable to read bulk assessment file:", error);
      toast.error("We couldn't read that file. Please upload an .xls, .xlsx, or .csv file.");
    }
  };

  const downloadBulkTemplate = () => {
    const link = document.createElement("a");

    link.href = `${process.env.PUBLIC_URL || ""}/templates/Bulk%20Upload%20Template.xlsx?v=2`;
    link.download = "Bulk Upload Template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Shared by the Create button and spreadsheet uploads so both show the same
  // errors under the same inputs.
  const getFormErrors = (assessmentType, respondent, observerList) => {
    const newErrors = {};
    // // console.log('assessmentType', assessmentType)
    if (!assessmentType?.length) {
      newErrors.assessmentType = "Assessment type is required.";
    }


    if (!respondent.name.trim()) {
      newErrors.name = "Respondent name is required.";
    } else {
      const nameError = validateName(respondent.name);
      if (nameError && nameError !== 'Please enter a valid name.') {
        newErrors.name = nameError;
      } else if (!nameRegex.test(respondent.name.trim())) {
        newErrors.name = "Please enter a valid name.";
      }
    }

    if (!respondent.email.trim()) {
      newErrors.email = "Respondent email is required.";
    } else if (!emailRegex.test(respondent.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    // // console.log(' observer list - ', observerList)

    if (assessmentType === "360 Assessment") {
      const observerErrors = {};
      // console.log("observer-----observerList", observerList)

      observerList.forEach((observer, idx) => {
        const nameOk = !!observer?.name?.trim();
        const emailOk = !!observer?.email?.trim();
        const roleOk = !!observer?.role?.trim();
        const trimmedEmail = observer?.email?.trim().toLowerCase();
        const selfEmail = respondent?.email?.trim().toLowerCase();
        const duplicate = observerList.some((o, i) => i !== idx && o.email.trim().toLowerCase() === trimmedEmail);
        const sameAsSelf = trimmedEmail === selfEmail;

        if (!nameOk) {
          observerErrors[`observer_${idx}_name`] = "Observer name is required.";
        } else {
          const observerNameError = validateName(observer.name);
          if (observerNameError && observerNameError !== 'Please enter a valid name.') {
            observerErrors[`observer_${idx}_name`] = observerNameError;
          } else if (!nameRegex.test(observer.name.trim())) {
            observerErrors[`observer_${idx}_name`] = "Please enter a valid name.";
          }
        }
        if (!emailOk) { observerErrors[`observer_${idx}_email`] = "Observer email is required."; }
        else if (!emailRegex.test(observer.email.trim())) {
          observerErrors[`observer_${idx}_email`] = "Please enter a valid email address.";
        } else if (sameAsSelf) {
          observerErrors[`observer_${idx}_email`] = "Email already used by the respondent.";
        } else if (duplicate) {
          observerErrors[`observer_${idx}_email`] = "Email already used for another observer.";
        }

        if (!roleOk) { observerErrors[`observer_${idx}`] = "Relationship is required."; }
      });

      if (Object.keys(observerErrors).length) newErrors.observers = observerErrors;
    }

    return newErrors;
  };

  const handleCreateAssessment = async (e, check) => {
    e.preventDefault();
    setIsSubmiting(true)
    if (errors.observer) {
      setIsSubmiting(false)
      return;
    }
    if (check) {
      const newErrors = getFormErrors(selectedAssessmentType, assessmentData, observersList);
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        setIsSubmiting(false);
        return;
      }
    }

    if (editForm) {
      const [selfFirstName, ...selfRest] = assessmentData.name.split(" ");
      const selfLastName = selfRest.join(" ");

      const parentPayload = {
        assessmentId: editForm.assessment_hash,
        deploymentId: clientId,
        designation: selectedAssessmentType,
        fullName: assessmentData.name,
        firstName: selfFirstName,
        lastName: selfLastName,
        email: assessmentData.email,
      };

      try {
        const parentRes = await updateAssessment(editForm.assessment_hash, parentPayload);

        if (parentRes?.status === "success") {
          if (selectedAssessmentType === "360 Assessment") {
            await Promise.all(
              observersList.map(async (ob) => {
                if (!ob.assessment_hash) return null; // Nothing to update

                const [first, ...lastArr] = (ob.name || "").split(" ");
                const obPayload = {
                  assessmentId: ob.assessment_hash,
                  deploymentId: clientId,
                  designation: ob.role,
                  fullName: ob.name,
                  firstName: first,
                  lastName: lastArr.join(" "),
                  email: ob.email,
                };

                return updateAssessment(ob.assessment_hash, obPayload);
              })
            );
          }

          toast.success("Assessment updated successfully.");
          await fetchApi();
          await updateClientData();
          setAssessmentStep(0);
        } else {
          setErrors((prev) => ({ ...prev, user: parentRes?.message || "Something went wrong" }));
        }
      } catch (err) {
        console.error("PUT failed:", err);
      } finally {
        setIsSubmiting(false);
      }

      return;
    }

    const is360 = selectedAssessmentType === "360 Assessment";


    // Bulk imports can retain an incomplete observer count; show that error
    // when Create is pressed instead of rejecting the spreadsheet up front.
    if (is360 && (observersList?.length < 4 || observersList?.length > 12)) {
      const tempErrors = { ...errors };

      tempErrors.observers = {
        ...tempErrors.observers,
        overall: observersList?.length < 4
          ? "Minimum 4 observers required"
          : "Maximum 12 observers allowed"
      };

      setErrors(tempErrors)
      setIsLoading(false)
      setIsSubmiting(false)
      return true;
    }


    const [firstName, ...lastNameArr] = assessmentData.name.trim().split(" ").filter(Boolean);
    const lastName = lastNameArr.join(" ");

    const userDetails = {
      fullName: assessmentData.name,
      firstName,
      lastName,
      email: assessmentData.email,
      nameType: "Self",
    };

    const baseRequestBody = {
      assessmentId: 78,
      userDetails,
      firstPage: "Page1",
      designation: selectedAssessmentType,
      hasParent: false,
      parentId: parseInt(authCtx?.orgId),
      deploymentId: clientId,
    };

    try {
      const parentCreateRes = await createAssessmentAndUser({
        ...baseRequestBody,
        "originalCreator": true
      });

      // console.log("parentCreateRes",parentCreateRes)

      if (parentCreateRes?.data?.status !== "success") {
        setErrors((prev) => ({ ...prev, user: parentCreateRes?.message || "Something went wrong" }));
        return;
      }

      await updateAssessmentStatus({ assessmentId: parentCreateRes?.data?.id, deploymentId: clientId, status: "inactive" });

      if (is360) {
        const observerResults = await Promise.all(
          observersList.map(async (ob) => {
            const [f, ...lArr] = ob.name.split(" ");
            const obUserDetails = {
              fullName: ob.name,
              firstName: f,
              lastName: lArr.join(" "),
              email: ob.email,
              nameType: "Self",
            };

            const obReqBody = {
              ...baseRequestBody,
              userDetails: obUserDetails,
              designation: ob.role,
              hasParent: true,
              parentId: parentCreateRes?.data?.id,
            };

            const res = await createAssessmentAndUser(obReqBody);
            if (res?.data?.status === "success") {
              await updateAssessmentStatus({ assessmentId: res.data.id, deploymentId: clientId, status: "inactive" });
              ob.assessment_hash = res.data.id;
            }
            return res;
          })
        );

        const observerErrors = observerResults.reduce((acc, r, idx) => {
          if (r?.data?.status !== "success") {
            acc[`observer_${idx}`] = r?.message || "Observer creation failed";
          }
          return acc;
        }, {});

        if (Object.keys(observerErrors).length) {
          setErrors((prev) => ({ ...prev, observers: { ...prev.observers, ...observerErrors } }));
          setIsSubmiting(false);
          return;
        }
      }

      await fetchApi();
      await updateClientData();
      if (bulkRows.length) {
        const [nextRow, ...remainingRows] = bulkRows;
        loadBulkRow(nextRow, remainingRows);
        toast.info(`${remainingRows.length + 1} uploaded assessment${remainingRows.length ? "s" : ""} remaining.`);
      } else {
        resetBulkUpload();
        setAssessmentStep(0);
      }
    } catch (err) {
      console.error("POST failed:", err);
    } finally {
      setIsSubmiting(false);
    }
  };

  const handleObserverChange = (event) => {
    const count = Math.min(4, parseInt(event.target.value) || 0);
    setObservers(event.target.value);
  };

  const handleObserverInputChange = (index, field, value) => {
    let newList = [...observersList];
    if (!newList[index]) {
      newList[index] = { name: "", email: "", showEmail: false, showFullName: false, role: "", assessment_hash: "" };
    }
    newList[index] = { ...newList[index], [field]: value };

    setObserversList(newList);
    
    // Validate the field and update errors
    setErrors((prevErrors) => {
      const fieldKey =
        field === "role"
          ? `observer_${index}`
          : `observer_${index}_${field}`;

      let errorMsg = '';
      
      if (field === 'name') {
        if (!value.trim()) {
          errorMsg = 'Observer name is required.';
        } else {
          const nameError = validateName(value);
          if (nameError && nameError !== 'Please enter a valid name.') {
            errorMsg = nameError;
          } else if (!nameRegex.test(value.trim())) {
            errorMsg = 'Please enter a valid Observer name.';
          }
        }
      } else {
        errorMsg = validateForm(field, value, emailRegex, newList, index);
      }

      return {
        ...prevErrors,
        observers: {
          ...prevErrors.observers,
          [fieldKey]: errorMsg,
        },
      };
    });
  };


  const refreshAssessments = async (assessmentId, assessmentType) => {
    try {

      const assessmentDetails = clientData?.assessments?.map((el) => {
        if (el?.designation === "360 Assessment") {
          return {
            ...el,
            members: [{ designation: "Self-Assessment", userName: el?.userName, email: el?.email, status: el?.status, assessment_hash: el?.assessment_hash }, ...(el.members || [])]
          };
        } else {
          return el;
        }
      })?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // // console.log("assessmentDetails", assessmentDetails)

      setAssessmentList(assessmentDetails);

      const updatedClient = await updateClientData();

      navigate(`/client/${clientId}?payment_sucess=${assessmentId}&type=${assessmentType}`, {
        state: { client: updatedClient },
        replace: true
      });

    } catch (error) {
      console.error("Error refreshing assessments:", error);
    }
  };

  const handleSameDetails = (e) => {
    const isChecked = e.target.checked;
    setIsSameAsClient(isChecked);

    if (isChecked) {
      setAssessmentData({
        name: clientData?.name || clientData?.title || "",
        email: clientData?.email || '',
      });
    } else {
      setAssessmentData({
        name: '',
        email: '',
      });
    }
  };

  const handleCreate = () => {
    setAssessmentStep(1)
    setErrors({})
    resetBulkUpload();
    setAssessmentData({
      name: "",
      email: "",
    });
    setObserversList([])
    setSelectedAssessmentType('')
  };

  const onFocus = (e, name) => {
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  }

  const handleFocus = (index) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors.observers) {
        delete newErrors.observers[`observer_${index}`];
      }
      return newErrors;
    });
  };

  const hasClientAssessment = assessmentList?.some((asmt) =>
    asmt.name?.toLowerCase() === (clientData?.name || "").toLowerCase() ||
    asmt.email?.toLowerCase() === (clientData?.email || "").toLowerCase()
  );

  // // console.log('assessmentList', assessmentList)

  const handleClose = () => {
    setAssessmentStep(0)
    setEditForm(false)
    resetBulkUpload();
  }

  const validateForm = (fieldName, value, emailRegex, observersListArray, index) => {
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          return 'Respondent name is required.';
        }
        return '';

      case 'email':
        const trimmedEmail = value.trim().toLowerCase();
        // Observer fields pass the observer list; the respondent field does not.
        const isObserver = Array.isArray(observersListArray);

        if (!trimmedEmail) {
          return isObserver ? 'Observer email is required.' : 'Respondent email is required.';
        }

        const selfEmail = assessmentData?.email?.trim().toLowerCase();
        const isSameAsSelf = isObserver && trimmedEmail === selfEmail;
        const isDuplicate = observersListArray?.some((observer, i) =>
          i !== index && observer?.email.trim().toLowerCase() === trimmedEmail
        );

        if (isSameAsSelf) {
          return 'Email already used by the respondent.';
        } else if (isDuplicate) {
          return 'Email already used for another observer.';
        } else if (!emailRegex.test(trimmedEmail)) {
          return 'Please enter a valid email address.';
        }

        return '';

      case 'role':
        return value.trim() ? '' : 'Relationship is required.';

      default:
        return '';
    }
  };


  const getCustomSelectStyles = () => ({
    "& .MuiSelect-select": {
      paddingRight: "14px !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "2px solid #5795B2",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    outline: "#5795B2",
    boxShadow: "none",
  });

  const getCustomMenuItemStyles = () => ({
    "&:hover": {
      backgroundColor: "#DDEAF0",
    },
    "&.Mui-selected": {
      color: "#DDEAF0",
      backgroundColor: "#426B88",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#DDEAF0",
      color: "#000000"
    },
  });

  const clientEmail = clientData?.email?.trim().toLowerCase() || "";
  const clientName = clientData?.name?.trim().toLowerCase() || "";

  const { hasSelf, has360 } = useMemo(() => {
    let self = false;
    let three = false;

    assessmentList?.forEach(a => {
      const matchesTop =
        a.email?.toLowerCase() === clientEmail ||
        a.userName?.toLowerCase() === clientName;

      if (a.designation === "Self-Assessment" && matchesTop) {
        self = true;
      }

      const matches360Members = a.members?.some(m =>
        m.email?.toLowerCase() === clientEmail ||
        m.userName?.toLowerCase() === clientName
      );

      if (
        (a.designation?.includes("360") && matchesTop) ||
        matches360Members
      ) {
        three = true;
      }
    });

    return { hasSelf: self, has360: three };
  }, [assessmentList, clientEmail, clientName]);

  const shouldHideCheckbox = hasSelf && has360;

  // logs 
  // // console.log('errors.user', errors.user)
  // // console.log('editForm', editForm)
  // // console.log("observersList-assessmentData", observersList)
  // // console.log('assessmentList - -', assessmentList)
  // // console.log('observers - - ', observers)
  // // console.log('asmt clientData', clientData)
  // // console.log('checkbox-visible?', !shouldHideCheckbox)
  // // console.log('setValue', setValue)

  return (
    <div>
      <div className="assessmentdetails">
        <h2>Assessment Details</h2>
        <AssessmentList assessmentList={assessmentList} setAssessmentList={setAssessmentList} updateClientData={updateClientData} selectedMembers={selectedMembers} showCheckbox={showCheckbox} setSelectedMembers={setSelectedMembers} setShowCheckbox={setShowCheckbox} clientId={clientId} isLoading={isLoading} refreshAssessments={refreshAssessments} editForm={editForm} setEditForm={setEditForm} setValue={setValue} clientData={clientData} />

        {isLoading ? (
          <div className="loading-spinner">
            <CircularProgress style={{ color: '#EAF1F5' }} size={40} />
          </div>
        ) : assessmentList?.length === 0 && assessmentStep === 0 ? (
          <div className="addassessmentdetails">
            <p className="pform">This client doesn’t have any assessments yet.</p>
            <p className="pform">Let’s create one to get their journey started!</p>
            <Button className="cta-assessment" onClick={() => setAssessmentStep(assessmentStep + 1)}>
              Create Assessment
            </Button>
          </div>
        ) : assessmentStep === 0 && assessmentList?.length > 0 ? (
          <div className="addmoreclient">
            <Button className="addmoreclient-cta" onClick={() => handleCreate()}>
              + Add another assessment{" "}
            </Button>
          </div>
        ) : null}


        {assessmentStep === 1 ? (
          <div className="assessment-form">
            {!editForm && selectedAssessmentType === "360 Assessment" && <div className="bulk-upload" aria-label="Bulk assessment upload">
              <div>
                <p className="pform">Bulk Upload</p>
                <ul className="bulk-upload-help">
                  <li>Accepted formats: .xls, .xlsx, .csv</li>
                  <li>
                    Complete all columns
                    <Tooltip
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                      title="Respondent Name, Respondent Email, Observer Name, Observer Email, and Observer Relationship with Respondent."
                    >
                      <InfoOutlined className="bulk-upload-info" tabIndex={0} aria-label="Required columns" />
                    </Tooltip>
                  </li>
                  <li>Enter one observer per row. Each respondent must have 4–12 observers.</li>
                </ul>
                {bulkFileName && <p className="bulk-upload-status">{bulkFileName} · {bulkTotal} respondent{bulkTotal === 1 ? "" : "s"} queued</p>}
                {bulkFileName && (
                  <div className="bulk-create-progress" aria-live="polite">
                    <div>
                      {isSubmiting
                        ? `Creating respondent ${bulkTotal - bulkRows.length} of ${bulkTotal}`
                        : `Next respondent ready · ${Math.max(0, bulkRows.length)} remaining after this one`}
                    <span>{bulkTotal - bulkRows.length} / {bulkTotal}</span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={bulkTotal ? ((bulkTotal - bulkRows.length) / bulkTotal) * 100 : 0}
                    />
                  </div>
                )}
              </div>
              <div className="bulk-upload-actions">
                <button className="bulk-template-button" type="button" onClick={downloadBulkTemplate}>Download Template</button>
                <button className="bulk-upload-button" type="button" onClick={() => bulkInputRef.current?.click()}>Upload Spreadsheet</button>
                <input ref={bulkInputRef} type="file" accept=".xls,.xlsx,.csv" onChange={handleBulkFile} hidden />
              </div>
            </div>}
            {!editForm && bulkIssues.length > 0 && (
              <div className="bulk-issues" role="alert">
                <div className="bulk-issues-header">
                  <p>
                    <strong>{bulkIssues.length} issue{bulkIssues.length === 1 ? "" : "s"} found in {bulkFileName}.</strong>{" "}
                    {`Fix ${bulkIssues.length === 1 ? "it" : "them"} in the form ${bulkTotal > 1 ? "as each respondent loads" : "below"}, or correct the file and upload it again.`}
                  </p>
                  <div className="bulk-issues-actions">
                    <button type="button" className="bulk-template-button" onClick={() => setShowBulkIssues((show) => !show)} aria-expanded={showBulkIssues}>
                      {showBulkIssues ? "Hide Issues" : "View Issues"}
                    </button>
                    <button type="button" className="bulk-template-button" onClick={downloadBulkIssues}>Download Error Report</button>
                  </div>
                </div>
                {bulkNeedsConfirm && (
                  <ul className="bulk-issues-list bulk-issues-confirm">
                    {bulkIssues.filter((issue) => issue.needsConfirm).map((issue, idx) => (
                      <li key={`confirm-${issue.row}-${idx}`}><span>Row {issue.row}</span>{issue.message}</li>
                    ))}
                    <li className="bulk-confirm-prompt">
                      Do you want to proceed?
                      <button type="button" className="bulk-template-button" onClick={() => setBulkNeedsConfirm(false)}>Proceed</button>
                      <button type="button" className="bulk-template-button" onClick={cancelBulkUpload}>Cancel</button>
                    </li>
                  </ul>
                )}
                {showBulkIssues && (
                  <ul className="bulk-issues-list">
                    {bulkIssues.map((issue, idx) => (
                      <li key={`${issue.row}-${idx}`}><span>Row {issue.row}</span>{issue.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="assessment-observers flex-aline">
              <div className="assessmenttype">
                <div className="close">
                  <Close onClick={handleClose} />
                </div>
                <p className="pform">Please select the assessment type:<span className="asterisk-mark">*</span></p>
                <Select className="inputform" required value={selectedAssessmentType}
                  onChange={(e) => {
                    const value = e.target.value; setSelectedAssessmentType(value);
                    if (errors.assessmentType && value !== "") {
                      setErrors((prev) => ({ ...prev, assessmentType: "" }));
                    }
                  }} displayEmpty IconComponent={() => null} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)}
                  sx={getCustomSelectStyles()}
                  renderValue={(selected) =>
                    selected ? selected : <span style={{ color: '#aaa' }}>Select assessment type</span>
                  }
                  endAdornment={
                    <img src={Dropdown} alt="dropdown" style={{ paddingRight: "10px", cursor: "pointer", marginTop: "3px", }} onClick={(e) => { e.stopPropagation(); setOpen(!open); }} />
                  }
                >
                  <MenuItem value="Self-Assessment" sx={getCustomMenuItemStyles()}>Self-Assessment</MenuItem>
                  <MenuItem value="360 Assessment" sx={getCustomMenuItemStyles()}>360 Assessment</MenuItem>
                </Select>
                {errors?.assessmentType ? (
                  <p className="error-text" style={{ margin: 0 }}>{errors.assessmentType}</p>
                ) : null}
              </div>
              {selectedAssessmentType == "360 Assessment" && (
                <div className="observers">
                  <p className="pform">Select the number of Observers:<span className="asterisk-mark">*</span> </p>
                  <Select className="inputform" required value={observers} onChange={handleObserverChange} displayEmpty IconComponent={() => null} open={observersOpen} onOpen={() => setObserversOpen(true)} onClose={() => setObserversOpen(false)} sx={getCustomSelectStyles()}
                    endAdornment={
                      <img src={Dropdown} alt="dropdown" style={{ paddingRight: "10px", cursor: "pointer", marginTop: "3px", }} onClick={(e) => { e.stopPropagation(); setObserversOpen(!open); }} />}
                  >
                    {Array.from({ length: 9 }, (_, index) => index + 4).map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
            </div>
            <div className="responder flex-aline">
              <div className="name">
                <p className="pform">Respondent Name<span className="asterisk-mark">*</span></p>
                <input className="inputform" type="text" value={assessmentData.name}
                  onChange={(e) => {
                    const name = e.target.value; setAssessmentData({ ...assessmentData, name });
                    if (errors.name) {
                      const errorMsg = validateForm('name', name, emailRegex);
                      setErrors((prev) => ({ ...prev, name: errorMsg }));
                    }
                  }}
                  placeholder="Type the respondent’s name"
                />
                {errors.name && <p className="error-text" style={{ margin: 0 }}>{errors.name}</p>}
              </div>
              <div className="email">
                <p className="pform">Respondent Email<span className="asterisk-mark">*</span></p>
                <input className="inputform" type="text" value={assessmentData.email}
                  onChange={(e) => {
                    const email = e.target.value; setAssessmentData({ ...assessmentData, email });
                    if (errors.email) {
                      const errorMsg = validateForm('email', email, emailRegex);
                      setErrors((prev) => ({ ...prev, email: errorMsg }));
                    }
                  }}
                  placeholder="Type the respondent’s email"
                  onFocus={(e) => { onFocus(e, 'user') }}
                />
                {errors.email && <p className="error-text" style={{ margin: 0 }}>{errors.email}</p>}
              </div>
            </div>
            {
              !shouldHideCheckbox && (
                <div className="checkbox-client">
                  <input
                    className="input-checkbox"
                    type="checkbox"
                    checked={isSameAsClient}
                    onChange={(e) => handleSameDetails(e)}
                  />
                  <p className="pform">Same as the client</p>
                </div>
              )
            }

            {errors?.user && selectedAssessmentType == "360 Assessment" ? (
              <p className="error-text" style={{ margin: 0 }}>{errors?.user}</p>
            ) : null}

            {selectedAssessmentType == "360 Assessment" && (
              <div className="observer-container">
                {[...Array(observers)].map((_, i) => (
                  <div key={i} className="observer flex-aline">
                    <div className="name">
                      <p className="pform">Observer #{i + 1} Name<span className="asterisk-mark">*</span>
                        {i === 0 && errors.observers?.overall && (
                          <span className="error-text-asmt-form" style={{ marginLeft: '10px', color: 'red', fontWeight: 'normal', fontSize: '16px', fontFamily: 'Lato, sans-serif' }}>{errors.observers.overall}</span>
                        )}
                      </p>
                      <input className="inputform" type="text" value={observersList[i]?.name || ""} placeholder="Type the observer’s name" name="name" onChange={(e) => handleObserverInputChange(i, "name", e.target.value)} />
                      {errors.observers?.[`observer_${i}_name`] && (
                        <p className="error-text" style={{ margin: 0 }}>{errors.observers[`observer_${i}_name`]}</p>
                      )}
                    </div>
                    <div className="observeremail">
                      <p className="pform">Observer #{i + 1} Email<span className="asterisk-mark">*</span></p>
                      <input className="inputform" type="text" value={observersList[i]?.email || ""} placeholder="Type the observer’s email" name="email" onFocus={() => handleFocus(i)} onChange={(e) => handleObserverInputChange(i, "email", e.target.value)} />
                      {errors.observers?.[`observer_${i}_email`] && (
                        <p className="error-text" style={{ margin: 0 }}>{errors.observers[`observer_${i}_email`]}</p>
                      )}
                    </div>
                    <div className="Relation">
                      <p className="pform">Relationship<span className="asterisk-mark">*</span></p>
                      <div style={{ position: "relative" }}>
                        <Select
                          className="inputform"
                          value={observersList[i]?.role || ""}
                          displayEmpty
                          IconComponent={() => null}
                          onChange={(e) => {
                            handleObserverInputChange(i, "role", e.target.value);
                            setRelationship(e.target.value);
                          }}
                          sx={{
                            backgroundColor: observersList[i]?.role ? "#5795B2" : "white",
                            color: "#fff",
                            "& .MuiSelect-icon": {
                              color: "#fff"
                            },
                            "&:hover": {
                              backgroundColor: "#5795B2"
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: "white",
                                "& .MuiMenuItem-root": {
                                  color: "#394554",
                                  "&:hover": {
                                    backgroundColor: "#DDEAF0"
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "#426B88",
                                    color: "#fff"
                                  },
                                  "&.Mui-selected:hover": {
                                    backgroundColor: "#426B88"
                                  }
                                }
                              }
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select
                          </MenuItem>
                          {["Peer", "Manager", "Direct Report", "Other"].map((value, index) => (
                            <MenuItem key={value} value={observersType[index]}>
                              {value}
                            </MenuItem>
                          ))}
                        </Select>
                        <img src={Dropdown} alt="dropdown" style={{ position: "absolute", right: "10px", top: "56%", transform: "translateY(-50%)", pointerEvents: "none", cursor: "pointer", }} />
                      </div>
                      {errors.observers?.[`observer_${i}`] && (
                        <p className="error-text" style={{ margin: 0 }}>
                          {errors.observers[`observer_${i}`] === "Invalid Designation"
                            ? "Relationship is required"
                            : errors.observers[`observer_${i}`]}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {errors?.user && selectedAssessmentType != "360 Assessment" ? (
              <p className="error-text" style={{ margin: 0 }}>{errors?.user}</p>
            ) : null}
            <div>
              <button className="cta-assessment" type="button" disabled={isSubmiting || bulkNeedsConfirm} onClick={(e) => {
                e.preventDefault();
                handleCreateAssessment(e, true)
              }}>
                {isSubmiting ? <CircularProgress size={20} style={{ color: "#fff" }} /> : <span>{editForm !== false ? "Update Assessment" : "Create Assessment"}</span>}
              </button>
              {
                // // console.log("errors", errors)
              }
            </div>
            {
              // console.log("errors",errors)
            }
            {/* {errors.user && (
              <p className="error-text-asmt-form">{errors.user}</p>
            )} */}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Assessment;
